// components/settings/Integrations.jsx
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Check,
  X,
  AlertCircle,
  RefreshCw,
  Link as LinkIcon,
  Unlink,
  Phone,
  Mail,
  Calendar,
  Users,
  DollarSign,
  Database,
} from "lucide-react";
import apiService from "../../services/api";

const Integrations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [callIntegration, setCallIntegration] = useState(null);
  const [hubspotIntegration, setHubspotIntegration] = useState(null);
  const [salesforceIntegration, setSalesforceIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    fetchIntegrations();
    handleOAuthCallback();
  }, []);

  const handleOAuthCallback = () => {
    const success = searchParams.get("success");
    const error = searchParams.get("error");

    if (success === "call-system-connected") {
      setNotification({
        type: "success",
        message: "Call system connected successfully!",
      });
      setSearchParams({});
      fetchIntegrations();
    } else if (error) {
      let errorMessage = "Connection failed";
      switch (error) {
        case "token_exchange_failed":
          errorMessage = "Failed to complete authentication";
          break;
        case "invalid_state":
          errorMessage = "Invalid authentication state";
          break;
        case "missing_params":
          errorMessage = "Missing authentication parameters";
          break;
        case "connection_failed":
          errorMessage = "Connection to call system failed";
          break;
        default:
          errorMessage = decodeURIComponent(error);
      }
      setNotification({ type: "error", message: errorMessage });
      setSearchParams({});
    }

    // Auto-dismiss notification
    if (success || error) {
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const [callRes, hubspotRes, salesforceRes] = await Promise.allSettled([
        apiService.getCallSystemIntegration(),
        apiService.getHubSpotStatus(),
        apiService.getSalesforceStatus(),
      ]);

      if (callRes.status === "fulfilled" && callRes.value.success) {
        setCallIntegration(callRes.value.integration);
      }
      if (hubspotRes.status === "fulfilled" && hubspotRes.value.success) {
        setHubspotIntegration(hubspotRes.value.integration);
      }
      if (salesforceRes.status === "fulfilled" && salesforceRes.value.success) {
        setSalesforceIntegration(salesforceRes.value.integration);
      }
    } catch (error) {
      console.error("Error fetching integrations:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0C0E12]">
        <RefreshCw className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Integrations</h1>
          <p className="text-gray-400">
            Connect your favorite tools and platforms
          </p>
        </div>

        {/* Notification */}
        {notification && (
          <div
            className={`mb-6 p-4 rounded-lg border flex items-start gap-3 ${
              notification.type === "success"
                ? "bg-green-500/10 border-green-500/30 text-green-400"
                : "bg-red-500/10 border-red-500/30 text-red-400"
            }`}
          >
            {notification.type === "success" ? (
              <Check size={20} className="flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className="font-medium">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="text-gray-400 hover:text-white"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Integration Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Call System Integration */}
          <IntegrationCard
            icon={Phone}
            title="Call Recording System"
            description="Sync call recordings and transcripts with your leads"
            isConnected={callIntegration?.isActive}
            connectedAt={callIntegration?.connectedAt}
            lastSync={callIntegration?.lastSyncAt}
            color="blue"
            onConfigure={() => (window.location.href = "/integrations/genzi")}
          />

          {/* HubSpot Integration */}
          <IntegrationCard
            icon={Database}
            title="HubSpot"
            description="Sync contacts, deals, and activities"
            isConnected={hubspotIntegration?.isActive}
            connectedAt={hubspotIntegration?.connectedAt}
            lastSync={hubspotIntegration?.lastSyncAt}
            color="orange"
            badge="CRM"
            onConfigure={() => (window.location.href = "/settings?tab=hubspot")}
          />

          {/* Salesforce Integration */}
          <IntegrationCard
            icon={DollarSign}
            title="Salesforce"
            description="Connect with Salesforce CRM"
            isConnected={salesforceIntegration?.isActive}
            connectedAt={salesforceIntegration?.connectedAt}
            lastSync={salesforceIntegration?.lastSyncAt}
            color="cyan"
            badge="CRM"
            onConfigure={() =>
              (window.location.href = "/integrations/salesforce")
            }
          />

          {/* Google Calendar (Coming Soon) */}
          <IntegrationCard
            icon={Calendar}
            title="Google Calendar"
            description="Sync appointments with your calendar"
            isConnected={false}
            color="red"
            badge="Coming Soon"
            disabled
          />

          {/* Slack (Coming Soon) */}
          <IntegrationCard
            icon={Mail}
            title="Slack"
            description="Get notifications in Slack channels"
            isConnected={false}
            color="purple"
            badge="Coming Soon"
            disabled
          />

          {/* Zoom (Coming Soon) */}
          <IntegrationCard
            icon={Users}
            title="Zoom"
            description="Schedule and manage Zoom meetings"
            isConnected={false}
            color="blue"
            badge="Coming Soon"
            disabled
          />
        </div>
      </div>
    </div>
  );
};

const IntegrationCard = ({
  icon: Icon,
  title,
  description,
  isConnected,
  connectedAt,
  lastSync,
  color,
  badge,
  onConfigure,
  disabled,
}) => {
  const colorMap = {
    blue: "from-blue-500 to-blue-600",
    orange: "from-orange-500 to-orange-600",
    cyan: "from-cyan-500 to-cyan-600",
    red: "from-red-500 to-red-600",
    purple: "from-purple-500 to-purple-600",
  };

  return (
    <div
      className={`bg-[#13161B] border border-[#22262F] rounded-xl p-6 ${
        disabled ? "opacity-50" : ""
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-12 h-12 rounded-lg bg-gradient-to-br ${colorMap[color]} flex items-center justify-center flex-shrink-0`}
          >
            <Icon size={24} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-white">{title}</h3>
              {badge && (
                <span className="px-2 py-0.5 text-xs font-medium bg-gray-700 text-gray-300 rounded">
                  {badge}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-400 mt-1">{description}</p>
          </div>
        </div>

        {isConnected && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/30">
            <Check size={12} className="mr-1" />
            Connected
          </span>
        )}
      </div>

      {isConnected && (
        <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-3 mb-4">
          <div className="grid grid-cols-2 gap-3 text-xs">
            {connectedAt && (
              <div>
                <div className="text-gray-400 mb-1">Connected</div>
                <div className="text-white">
                  {new Date(connectedAt).toLocaleDateString()}
                </div>
              </div>
            )}
            {lastSync && (
              <div>
                <div className="text-gray-400 mb-1">Last Sync</div>
                <div className="text-white">
                  {new Date(lastSync).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <button
        onClick={onConfigure}
        disabled={disabled}
        className={`w-full px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
          disabled
            ? "bg-gray-700 text-gray-400 cursor-not-allowed"
            : isConnected
            ? "bg-[#0C0E12] text-blue-400 border border-blue-500/30 hover:bg-blue-500/10"
            : "bg-blue-600 text-white hover:bg-blue-700"
        }`}
      >
        {disabled ? (
          "Coming Soon"
        ) : isConnected ? (
          <>
            <LinkIcon size={16} />
            Configure
          </>
        ) : (
          <>
            <LinkIcon size={16} />
            Connect
          </>
        )}
      </button>
    </div>
  );
};

export default Integrations;
